import React, { useRef, Suspense, useMemo, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber"; 
import { Container, Row, Col } from "react-bootstrap";
import { OrbitControls, Text, Line, Html } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import * as THREE from "three"; // 🔥 यह लाइन अब सही है
import "./PlatformEcosystem.css";
import { FintechSmartSolutions } from "./FintechSmartSolutions";

// Partner Labels (Same as before)
const partnerLabels = [
  "Strategic Partner",
  "Technology Partner",
  "Knowledge Partner",
  "Outreach Partner",
  "Banking Partner",
  "NBFC Partner",
  "Growth Partner",
  "Finance Partner",
  "Investment Partner",
  "Credit Partner",
];

// Central Hub
const CentralHub = () => {
  const meshRef = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (meshRef.current) {
        // Clockwise Rotation
        meshRef.current.rotation.y += 0.01; 
        meshRef.current.scale.setScalar(1 + Math.sin(t * 2) * 0.05);
    }
  });

  return (
    <group>
      <mesh ref={meshRef}>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial
          color="#00c9a7" 
          emissive="#00ffd0" 
          emissiveIntensity={1.2}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>

      {/* Outer glow ring */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.4, 0.05, 16, 100]} />
        <meshStandardMaterial
          color="#80ffdb"
          emissive="#80ffdb"
          emissiveIntensity={0.8}
        />
      </mesh>
    </group>
  );
};

// Orbiting Partner Nodes
const PartnerNodes = ({ radius = 6 }) => {
  const count = partnerLabels.length;
  
  const nodesRef = useRef(
    Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      return {
        id: i,
        position: new THREE.Vector3(x, (Math.random() - 0.3) * 2, z),
        label: partnerLabels[i],
      };
    })
  );

  const nodes = nodesRef.current;

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() * 0.2;
    nodes.forEach((node, i) => {
      const angle = (i / count) * Math.PI * 2 + t;
      node.position.x = Math.cos(angle) * radius;
      node.position.z = Math.sin(angle) * radius;
    });
  });

  return (
    <group>
      {nodes.map((node) => (
        <PartnerNode key={node.id} node={node} />
      ))}
      <ConnectionLines nodes={nodes} />
    </group>
  );
};

const PartnerNode = ({ node }) => {
  const textRef = useRef();
  const meshRef = useRef();

  useFrame(({ clock, camera }) => {
    if (textRef.current) {
      textRef.current.quaternion.copy(camera.quaternion);
    }
    
    if (meshRef.current) {
        meshRef.current.parent.position.copy(node.position);
    }
    
    const hue = (Math.sin(clock.getElapsedTime() * 0.5 + node.id) * 0.5 + 0.5) * 360;
    if (meshRef.current && meshRef.current.material) {
        meshRef.current.material.color.setHSL(hue / 360, 0.7, 0.5);
        meshRef.current.material.emissive.setHSL(hue / 360, 0.7, 0.4);
    }
  });

  return (
    <group> 
      <mesh ref={meshRef}>
        <boxGeometry args={[0.25, 0.25, 0.25]} />
        <meshStandardMaterial
          color="#0b7285"
          emissive="#0b7285"
          emissiveIntensity={0.6}
        />
      </mesh>

      <Text
        ref={textRef}
        position={[0, 0.5, 0]} 
        fontSize={0.16}
        color="#052c65" 
        anchorX="center"
        anchorY="middle"
      >
        {node.label}
      </Text>
    </group>
  );
};

const ConnectionLines = ({ nodes }) => {
  const lines = useMemo(
    () =>
      nodes.map((node) => ({
        points: [new THREE.Vector3(0, 0, 0), node.position], 
        speed: 1 + Math.random(),
      })),
    [nodes] 
  );

  return (
    <group>
      {lines.map((line, i) => (
        <PulsingLine key={i} line={line} />
      ))}
    </group>
  );
};

const PulsingLine = ({ line }) => {
  const ref = useRef();
  
  useFrame(({ clock }) => {
    if (ref.current) {
      const t = Math.sin(clock.getElapsedTime() * line.speed) * 0.5 + 0.5;
      ref.current.material.opacity = 0.3 + t * 0.5;
      
      ref.current.geometry.setFromPoints([line.points[0], line.points[1]]);
      ref.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <Line
      ref={ref}
      points={line.points}
      color="#63e6be" 
      lineWidth={2}
      transparent
      opacity={0.7}
    />
  );
};

// Scene with WebGL Context Loss Handler
const MatchGraphScene = () => {
    const { gl } = useThree(); 

    // WebGL Context Loss Handler for hard refresh stability
    useEffect(() => {
        const handleContextLost = (event) => {
            event.preventDefault();
            console.error("WebGL Context Lost. Forcing reload.");
            window.location.reload(); 
        };

        const canvas = gl.domElement;
        canvas.addEventListener("webglcontextlost", handleContextLost, false);

        return () => {
            canvas.removeEventListener("webglcontextlost", handleContextLost, false);
        };
    }, [gl]);

    return (
        <>
            <ambientLight intensity={1.2} color="#ffffff" />
            <directionalLight position={[10, 10, 5]} intensity={1.5} color="#f1f3f5" />

            <CentralHub />
            <PartnerNodes />

            <EffectComposer>
                <Bloom luminanceThreshold={0} luminanceSmoothing={0.9} intensity={1.2} />
            </EffectComposer>

            {/* Clockwise Rotation */}
            <OrbitControls autoRotate autoRotateSpeed={0.6} enableZoom={false} enablePan={false} />
        </>
    );
};

// MAIN SECTION
const PlatformEcosystem = () => {
    const [isSceneVisible, setIsSceneVisible] = useState(false);
    
    // Controlled mounting (50ms delay for quick visibility)
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsSceneVisible(true);
        }, 50); 

        return () => clearTimeout(timer);
    }, []);

  return (
    <section id="platform-ecosystem-section" className="platform-ecosystem-section py-4 py-md-5">
      <Container className="text-center mb-5">
        <h2 className="display-4 fw-bold mb-3 text-dark">
          Unified <span className="highlight-text">Financial Partner Ecosystem</span>
        </h2>
        <p className="lead text-sub-secondary mx-auto" style={{ maxWidth: "800px" }}>
          Securely connect with our network of RBI-registered <b>Partner Institutions</b>.  
          Our AI instantly maps your profile to the best available financial opportunity.
        </p>
      </Container>
      
      <div className="threejs-canvas-container">
        {isSceneVisible ? (
          <Canvas 
            camera={{ position: [0, 2, 9], fov: 55 }} 
            dpr={1} 
            gl={{ 
                powerPreference: "high-performance", 
                antialias: true, 
                preserveDrawingBuffer: true,
            }}
          > 
            <Suspense 
              fallback={
                <Html center>
                  <div className="loading-text">Loading 3D Graph...</div>
                </Html>
              }
            >
              <MatchGraphScene />
            </Suspense>
          </Canvas>
        ) : (
          <div className="loading-screen-placeholder">
              <div className="loading-text-large">Initializing 3D Ecosystem...</div>
          </div>
        )}
      </div>

      <Container className="mt-5">
        <Row>
          <Col>
            <div className="compliance-note-wrapper">
              <div className="compliance-note p-3 border border-info rounded-3 text-start">
                <p className="mb-0 small text-dark fw-bold">
                  <span className="text-info me-2">Compliance Statement:</span>
                  <FintechSmartSolutions /> is a Technology Platform and not a lender.  
                  All loan services are enabled through RBI-registered Banks/NBFCs.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default PlatformEcosystem;